import React, { useEffect, useState } from "react";
import RatingService from "../Rating/RatingService";
import appointmentsService from "../appointments/appointmentService";
import DoctorInfoService from "../doctorInfo/doctorInfoService";
import OfficeService from "../office/officeService";
import DiagnosisCombinerService from "../Authorization/diagnosisCombinerService";
import AuthorizationService from "../Authorization/authorizationService";
import patientInfoService from "../patientInfo/patientInfoService";
import "./AdminRatingView.css";

const RatingAdminView = () => {
  const [ratings, setRatings] = useState([]);
  const [appointments, setAppointments] = useState({});
  const [patients, setPatients] = useState({});
  const [doctors, setDoctors] = useState({});
  const [offices, setOffices] = useState({});
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [officeOptions, setOfficeOptions] = useState([]);

  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [selectedOffice, setSelectedOffice] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  const doctorService = new DoctorInfoService();
  const officeService = new OfficeService();
  const authService = new AuthorizationService();
  const diagService = new DiagnosisCombinerService();
  const patientService = new patientInfoService();
  const appointmentService = new appointmentsService();

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      const ratingsList = await RatingService.getAllRatings();
      setRatings(ratingsList);

      const doctorList = await doctorService.getAllDoctors();
      setDoctorOptions(doctorList);
      const officeList = await officeService.getAllOffices();
      setOfficeOptions(officeList);

      const apptMap = {};
      const docMap = {};
      const officeMap = {};
      const patientMap = {};

      for (const rating of ratingsList) {
        const appointmentId = rating.appointmentId;
        if (!appointmentId) continue;

        try {
          const appt = await appointmentService.getAppointmentById(appointmentId);
          apptMap[appointmentId] = appt;

          // Doctor
          if (appt.doctorId && !docMap[appt.doctorId]) {
            const doc = await doctorService.getDoctorById(appt.doctorId);
            docMap[appt.doctorId] = `${doc.firstname} ${doc.lastname}`;
          }

          // Office
          if (appt.officeId && !officeMap[appt.officeId]) {
            const off = await officeService.getOfficeById(appt.officeId);
            officeMap[appt.officeId] = off.officeName;
          }

          // Patient
          if (appt.diagCombId) {
            const diag = await diagService.getById(appt.diagCombId);
            const auth = await authService.findByAuthId(diag.authId);
            if (auth?.patientId && !patientMap[auth.patientId]) {
              const pat = await patientService.getPatientById(auth.patientId);
              patientMap[auth.patientId] = `${pat.firstName} ${pat.lastName}`;
              appt.patientId = auth.patientId; // store for easy access
              apptMap[appointmentId] = appt;
            }
          }
        } catch (err) {
          console.warn("Error loading related data:", err);
        }
      }

      setAppointments(apptMap);
      setDoctors(docMap);
      setOffices(officeMap);
      setPatients(patientMap);
    } catch (err) {
      console.error("Failed to load ratings:", err);
    }
  };

  const filteredRatings = ratings.filter((r) => {
    const appt = appointments[r.appointmentId];
    if (!appt) return false;

    const matchesDoctor = selectedDoctor ? appt.doctorId === selectedDoctor : true;
    const matchesOffice = selectedOffice ? appt.officeId === selectedOffice : true;
    const matchesDate = selectedDate ? appt.appointmentDate === selectedDate : true;

    return matchesDoctor && matchesOffice && matchesDate;
  });

  const exportCSV = () => {
    const rows = [
      ["Patient", "Doctor", "Experience Rating", "Office", "Office Rating", "Date", "Reason", "Notes", "Anonymous"],
      ...filteredRatings.map((r) => {
        const appt = appointments[r.appointmentId] || {};
        const doctor = doctors[appt.doctorId] || "Unknown";
        const office = offices[appt.officeId] || "Unknown";
        const patient = r.anoymonous ? "Anonymous" : patients[appt.patientId] || "Unknown";
        return [
          patient,
          doctor,
          r.rating,
          office,
          r.officeRating,
          appt.appointmentDate || "—",
          r.reason || "",
          r.extraNotes || "",
          r.anoymonous ? "Yes" : "No",
        ];
      }),
    ];

    const csv = rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "ratings_export.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="admin-rating-container">
      <h2>Rating Dashboard</h2>

      <div className="filters">
        <label>
          Filter by Doctor:
          <select value={selectedDoctor} onChange={(e) => setSelectedDoctor(e.target.value)}>
            <option value="">All</option>
            {doctorOptions.map((doc) => (
              <option key={doc.doctorId} value={doc.doctorId}>
                {doc.firstname} {doc.lastname}
              </option>
            ))}
          </select>
        </label>

        <label>
          Filter by Office:
          <select value={selectedOffice} onChange={(e) => setSelectedOffice(e.target.value)}>
            <option value="">All</option>
            {officeOptions.map((off) => (
              <option key={off.officeId} value={off.officeId}>
                {off.officeName}
              </option>
            ))}
          </select>
        </label>

        <label>
          Filter by Date:
          <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
        </label>

        <button className="csv-button" onClick={exportCSV}>Export CSV</button>
      </div>

      <table className="ratings-table">
        <thead>
          <tr>
            <th>Patient</th>
            <th>Doctor (Rating)</th>
            <th>Office (Rating)</th>
            <th>Date</th>
            <th>Reason</th>
            <th>Notes</th>
            <th>Anonymous</th>
          </tr>
        </thead>
        <tbody>
          {filteredRatings.map((r, i) => {
            const appt = appointments[r.appointmentId] || {};
            const doctorName = doctors[appt.doctorId] || "Unknown";
            const officeName = offices[appt.officeId] || "Unknown";
            const patientName = r.anoymonous ? "Anonymous" : patients[appt.patientId] || "Unknown";

            return (
              <tr key={i}>
                <td>{patientName}</td>
                <td>{`${doctorName}: ${r.rating || "-"}/10`}</td>
                <td>{`${officeName}: ${r.officeRating || "-"}/10`}</td>
                <td>{appt.appointmentDate || "—"}</td>
                <td>{r.reason || "—"}</td>
                <td>{r.extraNotes || "—"}</td>
                <td>{r.anoymonous ? "Yes" : "No"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default RatingAdminView;
